import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Building2, CalendarClock, CalendarDays, CheckCircle2, CircleAlert, ClipboardList,
  Filter, History, Loader2, Plus, SkipForward, UserRound, UserRoundX, X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { KpiCard } from "@/components/common/KpiCard";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime } from "@/lib/constants";

const todayIso = () => new Date().toISOString().slice(0, 10);

const StatusPill = ({ f }) => {
  if (f.status === "Completed")
    return <Badge variant="outline" className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="followup-status-badge">Completed</Badge>;
  if (f.status === "Skipped")
    return <Badge variant="outline" className="rounded-full bg-slate-100 text-slate-600 border-slate-200" data-testid="followup-status-badge">Skipped</Badge>;
  if (f.due_date < todayIso())
    return <Badge variant="outline" className="rounded-full bg-red-50 text-red-700 border-red-200" data-testid="followup-status-badge">Overdue</Badge>;
  if (f.due_date === todayIso())
    return <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200" data-testid="followup-status-badge">Due Today</Badge>;
  return <Badge variant="outline" className="rounded-full bg-sky-50 text-sky-700 border-sky-200" data-testid="followup-status-badge">Pending</Badge>;
};

const EMPTY_FILTERS = { date_from: "", date_to: "", recruiter_id: "all", company_id: "all", followup_type: "all", status: "all" };

export default function FollowUps() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || user.role === "super_admin";

  const [kpis, setKpis] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("my");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [types, setTypes] = useState({ application: [], employment: [] });
  const [companies, setCompanies] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [busy, setBusy] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [apps, setApps] = useState([]);
  const [createForm, setCreateForm] = useState({ application_id: "", followup_type: "", due_date: todayIso(), notes: "" });

  const [action, setAction] = useState(null); // { mode: 'complete'|'skip', f }
  const [actionNotes, setActionNotes] = useState("");
  const [skipReason, setSkipReason] = useState("");

  const [leftOf, setLeftOf] = useState(null); // followup whose candidate is being marked Left
  const [leftReason, setLeftReason] = useState("");

  const [historyOf, setHistoryOf] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    api.get("/followups/types").then((res) => setTypes(res.data)).catch(() => {});
    if (isAdmin) {
      api.get("/companies", { params: { limit: 100 } }).then((res) => setCompanies(res.data.items || res.data || [])).catch(() => {});
      api.get("/users/recruiters").then((res) => setRecruiters(res.data || [])).catch(() => {});
    }
  }, [isAdmin]);

  const load = useCallback(() => {
    setError(false);
    const params = {};
    if (tab === "overdue") params.bucket = "overdue";
    if (tab === "completed") params.bucket = "completed";
    if (tab === "my" && isAdmin) params.recruiter_id = filters.recruiter_id;
    if (tab === "all" && isAdmin && filters.recruiter_id !== "all") params.recruiter_id = filters.recruiter_id;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.company_id !== "all") params.company_id = filters.company_id;
    if (filters.followup_type !== "all") params.followup_type = filters.followup_type;
    if (filters.status !== "all" && tab !== "overdue" && tab !== "completed") params.status = filters.status;
    Promise.all([
      api.get("/followups", { params }),
      api.get("/followups/kpis", { params: isAdmin && filters.recruiter_id !== "all" ? { recruiter_id: filters.recruiter_id } : {} }),
    ])
      .then(([l, k]) => { setItems(l.data); setKpis(k.data); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [tab, filters, isAdmin]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const allTypes = useMemo(() => [...types.application, ...types.employment], [types]);
  const filtersActive = filters.date_from || filters.date_to || filters.recruiter_id !== "all" ||
    filters.company_id !== "all" || filters.followup_type !== "all" || filters.status !== "all";

  const openCreate = () => {
    setCreateForm({ application_id: "", followup_type: "", due_date: todayIso(), notes: "" });
    setCreateOpen(true);
    api.get("/applications").then((res) => setApps(res.data || [])).catch(() => setApps([]));
  };

  const submitCreate = async () => {
    if (!createForm.application_id) return toast.error("Choose an application");
    if (!createForm.followup_type) return toast.error("Choose a follow-up type");
    if (!createForm.due_date) return toast.error("Due date is required");
    setBusy(true);
    try {
      const res = await api.post("/followups", {
        application_id: createForm.application_id,
        followup_type: createForm.followup_type,
        due_date: createForm.due_date,
        notes: createForm.notes || null,
      });
      toast.success(`Follow-up ${res.data.fup_code} created`);
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitAction = async () => {
    if (action.mode === "skip" && skipReason.trim().length < 3) return toast.error("A skip reason is mandatory");
    setBusy(true);
    try {
      if (action.mode === "complete") {
        await api.put(`/followups/${action.f.id}/complete`, { notes: actionNotes || null });
        toast.success(`Follow-up ${action.f.fup_code} completed`);
      } else {
        await api.put(`/followups/${action.f.id}/skip`, { reason: skipReason.trim(), notes: actionNotes || null });
        toast.success(`Follow-up ${action.f.fup_code} skipped`);
      }
      setAction(null); setActionNotes(""); setSkipReason("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openHistory = (f) => {
    setHistoryOf(f);
    setHistoryLoading(true);
    api.get(`/followups/${f.id}/history`)
      .then((res) => setHistory(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setHistoryLoading(false));
  };

  const submitLeft = async () => {
    setBusy(true);
    try {
      const payload = { status: "Left" };
      if (leftReason.trim()) payload.reason = leftReason.trim();
      await api.put(`/employment/${leftOf.employment_id}/status`, payload);
      toast.success(`${leftOf.candidate?.full_name || "Candidate"} marked Left — pending follow-ups auto-closed`);
      setLeftOf(null); setLeftReason("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Follow-up Management</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {isAdmin ? "Track every candidate and employment follow-up across the team." : "Stay on top of your candidate and employment follow-ups."}
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-lg shrink-0" data-testid="followup-create-button">
          <Plus className="h-4 w-4 mr-2" /> New Follow-up
        </Button>
      </div>

      {/* Dashboard cards */}
      {kpis && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Due Today" value={kpis.due_today} icon={CalendarClock} tone="warning" testId="followup-kpi-due-today" />
          <KpiCard label="Overdue" value={kpis.overdue} icon={CircleAlert} tone="violet" testId="followup-kpi-overdue" />
          <KpiCard label="Upcoming" value={kpis.upcoming} icon={CalendarDays} tone="primary" testId="followup-kpi-upcoming" />
          <KpiCard label="Completed" value={kpis.completed} icon={CheckCircle2} tone="success" testId="followup-kpi-completed" />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-lg flex-wrap h-auto">
          <TabsTrigger value="my" className="rounded-md" data-testid="followup-tab-my">My Follow-ups</TabsTrigger>
          <TabsTrigger value="overdue" className="rounded-md" data-testid="followup-tab-overdue">Overdue</TabsTrigger>
          <TabsTrigger value="completed" className="rounded-md" data-testid="followup-tab-completed">Completed</TabsTrigger>
          {isAdmin && <TabsTrigger value="all" className="rounded-md" data-testid="followup-tab-all">All</TabsTrigger>}
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="rounded-xl border-border shadow-none" data-testid="followup-filters">
        <CardContent className="p-4">
          <div className="flex flex-col xl:flex-row xl:items-end gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground shrink-0 xl:pb-2.5">
              <Filter className="h-4 w-4" /> Filters
            </div>
            <div className={`grid grid-cols-2 ${isAdmin ? "lg:grid-cols-6" : "lg:grid-cols-4"} gap-3 flex-1`}>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From (due date)</Label>
                <Input type="date" className="bg-white h-9" value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="followup-filter-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" className="bg-white h-9" value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="followup-filter-to" />
              </div>
              {isAdmin && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Recruiter</Label>
                  <Select value={filters.recruiter_id} onValueChange={(v) => setFilters({ ...filters, recruiter_id: v })}>
                    <SelectTrigger className="bg-white h-9" data-testid="followup-filter-recruiter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All recruiters</SelectItem>
                      {recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isAdmin && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Company</Label>
                  <Select value={filters.company_id} onValueChange={(v) => setFilters({ ...filters, company_id: v })}>
                    <SelectTrigger className="bg-white h-9" data-testid="followup-filter-company"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All companies</SelectItem>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Type</Label>
                <Select value={filters.followup_type} onValueChange={(v) => setFilters({ ...filters, followup_type: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="followup-filter-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {allTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="followup-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Skipped">Skipped</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {filtersActive && (
              <Button variant="ghost" size="sm" className="rounded-lg shrink-0" onClick={() => setFilters(EMPTY_FILTERS)} data-testid="followup-filter-reset">
                <X className="h-3.5 w-3.5 mr-1.5" /> Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {error ? (
        <Card className="rounded-xl border-border shadow-none p-10 flex flex-col items-center gap-3" data-testid="followups-error">
          <p className="text-sm text-muted-foreground">Could not load follow-ups.</p>
          <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={load}>Retry</Button>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No follow-ups here"
          description="Follow-ups appear automatically when candidates join, or create one manually for an application." />
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Recruiter Remark</TableHead>
                  <TableHead>Assigned Recruiter</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((f) => (
                  <TableRow key={f.id} className="hover:bg-muted/60" data-testid="followup-row">
                    <TableCell className="font-mono text-xs">{f.fup_code}</TableCell>
                    <TableCell>
                      <p className="font-medium text-sm">{f.candidate?.full_name || "—"}</p>
                      <p className="text-xs text-muted-foreground font-mono">{f.candidate?.code}</p>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><Building2 className="h-3 w-3 text-muted-foreground" />{f.company_label || "—"}</span>
                    </TableCell>
                    <TableCell className="text-sm">{f.followup_type}</TableCell>
                    <TableCell className="text-sm">{formatDate(f.due_date)}</TableCell>
                    <TableCell><StatusPill f={f} /></TableCell>
                    <TableCell className="text-sm max-w-[240px]">
                      {f.recruiter_remark ? (
                        <div data-testid={`followup-remark-cell-${f.id}`}>
                          <p className="whitespace-pre-line line-clamp-2">{f.recruiter_remark}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{f.remark_by} · {formatDateTime(f.remark_at)}</p>
                        </div>
                      ) : f.notes ? (
                        <p className="text-muted-foreground line-clamp-2" data-testid={`followup-remark-cell-${f.id}`}>{f.notes}</p>
                      ) : f.skip_reason ? (
                        <p className="text-muted-foreground line-clamp-2" data-testid={`followup-remark-cell-${f.id}`}>Skip: {f.skip_reason}</p>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><UserRound className="h-3 w-3 text-muted-foreground" />{f.recruiter?.name || "—"}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {f.status === "Pending" && (
                          <>
                            <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white"
                              onClick={() => { setAction({ mode: "complete", f }); setActionNotes(""); }}
                              data-testid={`followup-complete-${f.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Complete
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 rounded-lg"
                              onClick={() => { setAction({ mode: "skip", f }); setActionNotes(""); setSkipReason(""); }}
                              data-testid={`followup-skip-${f.id}`}>
                              <SkipForward className="h-3.5 w-3.5 mr-1.5" /> Skip
                            </Button>
                            {isAdmin && f.employment_id && ["Joined", "Working"].includes(f.employment?.status) && (
                              <Button size="sm" variant="ghost" className="h-8 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => { setLeftOf(f); setLeftReason(""); }}
                                data-testid={`followup-mark-left-${f.id}`}>
                                <UserRoundX className="h-3.5 w-3.5 mr-1.5" /> Left
                              </Button>
                            )}
                          </>
                        )}
                        <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => openHistory(f)} data-testid={`followup-history-${f.id}`}>
                          <History className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Create dialog (manual application follow-up) */}
      <Dialog open={createOpen} onOpenChange={(v) => !v && setCreateOpen(false)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[90vh] overflow-y-auto" data-testid="followup-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New application follow-up</DialogTitle>
            <DialogDescription>
              Employment follow-ups (20/40/60/90 day) are created automatically when a candidate joins.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Application</Label>
              <Select value={createForm.application_id} onValueChange={(v) => setCreateForm({ ...createForm, application_id: v })}>
                <SelectTrigger className="bg-white" data-testid="followup-application-select"><SelectValue placeholder="Choose application" /></SelectTrigger>
                <SelectContent>
                  {apps.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {(a.candidate?.full_name || a.member?.name || "—")} · {(a.app_code || a.code)} · {a.job?.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {apps.length === 0 && <p className="text-[11px] text-muted-foreground">No applications available.</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Follow-up type</Label>
              <Select value={createForm.followup_type} onValueChange={(v) => setCreateForm({ ...createForm, followup_type: v })}>
                <SelectTrigger className="bg-white" data-testid="followup-type-select"><SelectValue placeholder="Choose type" /></SelectTrigger>
                <SelectContent>
                  {types.application.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Due date</Label>
              <Input type="date" className="bg-white" value={createForm.due_date}
                onChange={(e) => setCreateForm({ ...createForm, due_date: e.target.value })} data-testid="followup-due-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea rows={2} className="bg-white" value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} data-testid="followup-notes-input" />
            </div>
            <Button onClick={submitCreate} disabled={busy} className="w-full rounded-lg" data-testid="followup-create-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create follow-up
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete / Skip dialog */}
      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="followup-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {action?.mode === "complete" ? "Complete follow-up" : "Skip follow-up"} — {action?.f.fup_code}
            </DialogTitle>
            <DialogDescription>
              {action?.mode === "complete"
                ? "Marks the follow-up as completed. This cannot be undone."
                : "Skipping requires a reason. This is recorded permanently."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {action?.mode === "skip" && (
              <div className="space-y-1.5">
                <Label>Skip reason <span className="text-destructive">*</span></Label>
                <Textarea rows={2} className="bg-white" value={skipReason} placeholder="Why is this follow-up being skipped? (mandatory)"
                  onChange={(e) => setSkipReason(e.target.value)} data-testid="followup-skip-reason-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea rows={2} className="bg-white" value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)} data-testid="followup-action-notes-input" />
            </div>
            <Button onClick={submitAction} disabled={busy} className="w-full rounded-lg" data-testid="followup-action-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {action?.mode === "complete" ? "Mark Completed" : "Skip Follow-up"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark Left dialog (admin) */}
      <Dialog open={!!leftOf} onOpenChange={(v) => !v && setLeftOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="followup-left-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Mark candidate Left — {leftOf?.candidate?.full_name}</DialogTitle>
            <DialogDescription>
              Closes employment {leftOf?.employment?.emp_code} and its application, and auto-closes ALL remaining
              pending milestone follow-ups with reason “Candidate Left”. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Reason / note <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea rows={2} className="bg-white" value={leftReason} placeholder="Context recorded in the employment history..."
                onChange={(e) => setLeftReason(e.target.value)} data-testid="followup-left-reason-input" />
            </div>
            <Button onClick={submitLeft} disabled={busy} variant="destructive" className="w-full rounded-lg" data-testid="followup-left-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Mark Left & close follow-ups
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[85vh] overflow-y-auto" data-testid="followup-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">History — {historyOf?.fup_code}</DialogTitle>
            <DialogDescription>Immutable log of every change. Never edited or deleted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            {historyLoading && <Skeleton className="h-24 w-full rounded-lg" />}
            {!historyLoading && history.map((e) => (
              <div key={e.id} className="rounded-lg border border-border p-3" data-testid="followup-history-item">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    {e.event_type === "FOLLOWUP_CREATED" ? "Created" : "Status change"}
                  </span>
                  <span className="text-[11px] text-muted-foreground font-mono">{formatDateTime(e.at)}</span>
                </div>
                {e.previous_status && (
                  <p className="text-sm mt-1.5">
                    {e.previous_status}<span className="text-muted-foreground mx-1.5">→</span>
                    <span className="font-medium">{e.new_status}</span>
                  </p>
                )}
                {e.skip_reason && <p className="text-xs text-amber-700 mt-1">Skip reason: {e.skip_reason}</p>}
                {e.notes && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{e.notes}</p>}
                <p className="text-[11px] text-muted-foreground mt-1.5">by <span className="font-medium text-foreground">{e.actor_name}</span></p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
